import type { BarSpan } from "./DensityChart.lib";
import type { DensityChartBucketUnit } from "./densityBuckets.lib";
import type { WindowDragMode } from "./windowSpan.lib";
import { dragSpan, shiftSpan, snapSpan } from "./windowSpan.lib";

export interface DragOrigin {
    mode: WindowDragMode;
    /** In bars from the epoch, so it survives the strip redrawing mid-drag. */
    span: BarSpan;
    pointerId: number;
    /** The bars' unit when pressed; the drag is dropped once it changes. */
    unit: DensityChartBucketUnit;
    clientX: number;
    lastClientX: number;
    trackRect: DOMRect;
    pitchPx: number;
    /** The strip's offset when pressed. */
    offset: number;
    hasMoved: boolean;
    /** The edges in view when pressed, which stay in view while the strip pans. */
    keepsInView: { start: boolean; end: boolean };
}

interface PressOriginParams {
    mode: WindowDragMode;
    press: Pick<PointerEvent, "pointerId" | "clientX">;
    trackRect: DOMRect;
    /** The window, in bars from `firstBar`. */
    span: BarSpan;
    unit: DensityChartBucketUnit;
    barCount: number;
    minBar: number;
    firstBar: number;
    offset: number;
    /** Where the bars in view start and end, in bars from `firstBar`. */
    visible: { start: number; end: number };
}

/**
 * Where a press starts a drag. A drawn range starts at the bar position
 * pressed, with no width, and none starts in the zone before the start or
 * past today (null); one pressed on the days of the history start's bar
 * before it draws from the history start.
 */
export function pressOrigin({
    mode,
    press,
    trackRect,
    span,
    unit,
    barCount,
    minBar,
    firstBar,
    offset,
    visible,
}: PressOriginParams): DragOrigin | null {
    const pitchPx = trackRect.width / barCount;
    const pressBar = (press.clientX - trackRect.left) / pitchPx;
    const isDraw = mode === "draw";
    if (isDraw && (pressBar < Math.floor(minBar) || pressBar >= barCount)) {
        return null;
    }
    return {
        pointerId: press.pointerId,
        unit,
        clientX: press.clientX,
        lastClientX: press.clientX,
        trackRect,
        pitchPx,
        offset,
        hasMoved: false,
        keepsInView: {
            start: isDraw || span.start >= visible.start,
            end: isDraw || span.end <= visible.end,
        },
        mode,
        span: shiftSpan(
            isDraw ? { start: pressBar, end: pressBar } : span,
            firstBar,
        ),
    };
}

interface DragFromOriginParams {
    origin: DragOrigin;
    firstBar: number;
    offset: number;
    barCount: number;
    minBar: number;
    visible: { start: number; end: number };
}

/**
 * Where the pointer has taken the pressed span (`next`) and where a release
 * there would land (`landing`), in bars from `firstBar`, counting how far the
 * strip has panned since the press.
 */
export function dragFromOrigin({
    origin,
    firstBar,
    offset,
    barCount,
    minBar,
    visible,
}: DragFromOriginParams) {
    const from = shiftSpan(origin.span, -firstBar);
    const deltaBars =
        (origin.lastClientX - origin.clientX) / origin.pitchPx -
        (offset - origin.offset);
    const next = dragSpan({
        span: from,
        mode: origin.mode,
        deltaBars,
        barCount,
        minBar,
        inView: {
            start: origin.keepsInView.start ? visible.start : undefined,
            end: origin.keepsInView.end ? visible.end : undefined,
        },
    });
    const landing = snapSpan({
        span: next,
        from,
        mode: origin.mode,
        barCount,
        minBar,
    });
    return { next, landing };
}
